import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react'

import { VideoStatusContext } from 'UI/Components'
import { StepsStatusBox } from './StepsStatusBox'

import { delay, round } from 'modules'
import { PlaybookType } from 'app/types'

export enum StepsStatus {
    idle = 'idle',
    generating = 'generating',
    typing = 'typing',
    done = 'done',
    skipped = 'skipped'
}

const initialState = {
    status: StepsStatus.idle,
    stepsProgress: {} as Record<number, number>
}

export const StepsContext = createContext(initialState)

type Props = {
    children: ReactNode
    playbook: PlaybookType
}

export const StepsProvider = ({ playbook, children }: Props) => {
    const { services, revalidatePlaybook } = useContext(VideoStatusContext)

    const [status, setStatus] = useState(StepsStatus.idle)
    const [stepsProgress, setStepsProgress] = useState<(typeof initialState)['stepsProgress']>({})

    // Once every step inside stepsProgress prop has progress 100% - change status to DONE
    useEffect(() => {
        if (!Object.values(stepsProgress).length) return

        const finishedAnimation = Object.values(stepsProgress).every(v => v === 100)
        if (finishedAnimation) delay(2000).then(() => setStatus(StepsStatus.done))
    }, [setStatus, stepsProgress])

    // Set step status based on status of video service (VideoStatusProvider)
    // to show animation of typing (MagicDescription.tsx) when we use openAI to generate step description (note)
    useEffect(() => {
        if (Object.keys(services).length === 0) return

        switch (services.qg_enrich) {
            case 'pending':
            case 'in_progress':
                if (status !== StepsStatus.generating) setStatus(StepsStatus.generating)
                return
            case 'done':
                if (status === StepsStatus.idle) return setStatus(StepsStatus.skipped)

                if (status === StepsStatus.generating) {
                    revalidatePlaybook().then(() => setStatus(StepsStatus.typing))
                    return
                }
                return
            default: // undefined, skipped, error
                if (status !== StepsStatus.skipped) setStatus(StepsStatus.skipped)
                return
        }
    }, [revalidatePlaybook, services, services.qg_enrich, setStatus, status])

    useEffect(() => {
        if (status !== StepsStatus.typing) return

        const values = Object.values(stepsProgress)
        if (values.length > 0 && values.every(v => v === 100)) return

        delay(50).then(() => {
            const nextProgress = playbook.steps.reduce((acc, curr, idx) => {
                if (values[idx] === 100) return { ...acc, [idx]: 100 }
                if (!curr.note) return { ...acc, [idx]: 0 }

                const charPercent = round(100 / curr.note.length)
                return {
                    ...acc,
                    [idx]: Math.min(100, round((values[idx] || 0) + charPercent))
                }
            }, {})
            setStepsProgress(nextProgress)
        })
    }, [playbook.steps, stepsProgress, status])

    const contextValue = useMemo(
        () => ({
            status,
            stepsProgress
        }),
        [status, stepsProgress]
    )

    return (
        <StepsContext.Provider value={contextValue}>
            <StepsStatusBox playbook={playbook} />
            {children}
        </StepsContext.Provider>
    )
}
